import { PropertyTypeEnum } from "api/real-estate";
import { BranchCode } from "api/risk";
import { useAppSelector } from "app/hooks";
import { useAffectedQuotesConfirm } from "components/domain/AffectedQuotes";
import { useRgiRiskData, useUpiRiskData } from "features/subscription-process/contexts/selectors/selectors";
import { ContextCode, SurveyName, useGenericSurvey } from "features/subscription-process/profiling-process/hooks/useGenericSurvey";
import { selectAllQuestions } from "features/subscription-process/profiling-process/slices/genericSelectors";
import { IconPossibleAnswer } from "features/subscription-process/profiling-process/types/interfaces";
import { isIconAnswer, isLabelAnswer } from "features/subscription-process/profiling-process/types/possibleAnswer";
import { FormikProps } from "formik";
import { usePanel } from "hooks/usePanel";
import { useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { realEstateAPI } from "services/swagger-api/realEstateAPI";
import { rgiRealEstateFormConfig, upiRealEstateFormConfig } from "../form/realEstateFormConfig";
import { mapRealEstate } from "../mappers/mapRealEstate";
import { mapRealEstateFormValuesToCreateDto, mapRealEstateFormValuesToUpdateDto } from "../mappers/mapRealEstateFormValues";
import { RealEstateFormValues, RgiRealEstateFormValues, UpiRealEstateFormValues } from "../types/realEstateFormValues";
import { useRealEstateRequest } from "./useRealEstateRequest";

interface UseRealEstateFormProps {
  realEstateId?: string;
  riskId?: string;
  /** Si défini, la suppression retire le bien de ce parc uniquement (sans délier de la personne) */
  parcId?: string;
  refreshRealEstates?: () => void;
  branchCode: BranchCode
  formikRef: React.RefObject<FormikProps<RealEstateFormValues>>
  isFormValid: boolean
  hasFormChanges: boolean
}

const propertyTypeQuestionKey = "typeBien"

export const useRealEstateForm = ({
  realEstateId,
  riskId,
  parcId,
  refreshRealEstates,
  branchCode,
  formikRef,
  isFormValid,
  hasFormChanges
}: UseRealEstateFormProps) => {
  const { getSurvey, mapToSurveyFormValue, transformFormValues } = useGenericSurvey()
  const { fetchRealEstateById, realEstate, questionAnswers } = useRealEstateRequest(branchCode)
  const surveyQuestions = useAppSelector(selectAllQuestions)

  const { setPanelActions, closePanel } = usePanel();

  const upiRiskData = useUpiRiskData()
  const rgiRiskData = useRgiRiskData()
  const person = branchCode === BranchCode.RGI ? rgiRiskData?.person : upiRiskData?.person

  const isEditMode = !!realEstateId;

  // Résout la `possibleAnswer` (avec son vrai `id` backend) de la question `typeBien`
  // correspondant à un `PropertyTypeEnum`. Les clés du survey correspondent à la valeur
  // numérique de l'enum (House=1, Apartment=2, Office=3, Commercial=4).
  const resolveTypeBienAnswer = useCallback(
    (propertyType: PropertyTypeEnum): IconPossibleAnswer | undefined => {
      const typeBienQuestion = surveyQuestions.find((q) => q.key === propertyTypeQuestionKey)
      if (!typeBienQuestion) return undefined
      const match = typeBienQuestion.possibleAnswers.find(
        (pa) => (isIconAnswer(pa) || isLabelAnswer(pa)) && String(pa.key) === String(propertyType)
      )
      return match as IconPossibleAnswer | undefined
    },
    [surveyQuestions]
  )

  // Garantit que `surveyForm.typeBien` est cohérent avec le `propertyType` choisi avant
  // d'envoyer le payload : le back utilise la réponse survey, donc une réponse survey
  // résiduelle (chargée depuis l'API ou stale après changement d'onglet) écraserait la
  // sélection de l'utilisateur sinon.
  const reconcileTypeBienInSurveyForm = useCallback(
    (values: RealEstateFormValues, propertyType: PropertyTypeEnum): RealEstateFormValues => {
      if (!('surveyForm' in values) || !values.surveyForm) return values
      const coherent = resolveTypeBienAnswer(propertyType)
      if (!coherent) return values
      const existing = values.surveyForm[propertyTypeQuestionKey]?.[0] as IconPossibleAnswer | undefined
      if (existing && existing.id === coherent.id) return values
      return {
        ...values,
        surveyForm: {
          ...values.surveyForm,
          [propertyTypeQuestionKey]: [coherent],
        },
      }
    },
    [resolveTypeBienAnswer]
  )

  const executeSubmit = useCallback(async (): Promise<void> => {
    try {
      const rawValues = formikRef.current?.values;
      if (!rawValues) return;

      const survey = await getSurvey(ContextCode.RealEstate, SurveyName.RealEstate)

      // Source de vérité prioritaire : le champ Formik `propertyType`, alimenté par les
      // deux onglets via la sync bidirectionnelle. `surveyForm.typeBien` ne sert que de
      // fallback si Formik n'a rien.
      let propertyTypeAnswerValue: PropertyTypeEnum | undefined = rawValues.propertyType

      if (propertyTypeAnswerValue == null && 'surveyForm' in rawValues && rawValues.surveyForm) {
        const typeBienAnswer = rawValues.surveyForm[propertyTypeQuestionKey]?.[0] as IconPossibleAnswer | undefined
        if (typeBienAnswer?.key != null) {
          propertyTypeAnswerValue = parseInt(typeBienAnswer.key as string) as PropertyTypeEnum
        }
      }

      propertyTypeAnswerValue = propertyTypeAnswerValue ?? PropertyTypeEnum.House

      const values = reconcileTypeBienInSurveyForm(rawValues, propertyTypeAnswerValue)
      const transformedValues = transformFormValues(values);

      if (!person?.id) {
        throw new Error("Insured ID not found");
      }

      if (!riskId) {
        throw new Error("There is no risk to attach the real estate");
      }

      if (realEstateId) {
        const request = mapRealEstateFormValuesToUpdateDto(
          riskId,
          transformedValues,
          survey.id,
          person.id,
          realEstateId,
          propertyTypeAnswerValue || transformedValues.propertyType
        );
        await realEstateAPI.updateRealEstateWithQuestions(request);
      } else {
        const request = mapRealEstateFormValuesToCreateDto(
          riskId,
          transformedValues,
          survey.id,
          person.id,
          propertyTypeAnswerValue,
          realEstate && realEstate.rentAmount,
          realEstate && realEstate.charges,
        );
        await realEstateAPI.createRealEstateWithQuestions(request);
      }

      refreshRealEstates && refreshRealEstates();
      closePanel();
    } catch (error: any) {
      console.error("Error submitting real estate:", error);
      toast.error(error?.response?.error?.message || "An error occurred");
    }
  }, [getSurvey, person, realEstate, realEstateId, refreshRealEstates, transformFormValues, riskId, formikRef, closePanel, reconcileTypeBienInSurveyForm]);

  const fetchAffectedQuotes = useCallback(async () => {
    if (!realEstateId) return undefined;
    try {
      return await realEstateAPI.getAffectedQuotesByRealEstateId({ realEstateId });
    } catch (error) {
      console.error("Error fetching affected quotes:", error);
      return undefined;
    }
  }, [realEstateId]);

  const { showConfirmationDialog } = useAffectedQuotesConfirm({
    entityType: 'realEstate',
    fetchAffectedQuotes,
    onConfirm: executeSubmit,
    isEditMode
  });

  const submitRealEstate = useCallback(async (rawValues: RealEstateFormValues): Promise<void> => {
    try {
      const survey = await getSurvey(ContextCode.RealEstate, SurveyName.RealEstate)

      // Source de vérité prioritaire : le champ Formik `propertyType`, alimenté par les
      // deux onglets via la sync bidirectionnelle. `surveyForm.typeBien` ne sert que de
      // fallback si Formik n'a rien.
      let propertyTypeAnswerValue: PropertyTypeEnum | undefined = rawValues.propertyType

      if (propertyTypeAnswerValue == null && 'surveyForm' in rawValues && rawValues.surveyForm) {
        const typeBienAnswer = rawValues.surveyForm[propertyTypeQuestionKey]?.[0] as IconPossibleAnswer | undefined
        if (typeBienAnswer?.key != null) {
          propertyTypeAnswerValue = parseInt(typeBienAnswer.key as string) as PropertyTypeEnum
        }
      }

      propertyTypeAnswerValue = propertyTypeAnswerValue ?? PropertyTypeEnum.House

      const values = reconcileTypeBienInSurveyForm(rawValues, propertyTypeAnswerValue)
      const transformedValues = transformFormValues(values);

      if (!person?.id) {
        throw new Error("Insured ID not found");
      }

      if (!riskId) {
        throw new Error("There is no risk to attach the real estate");
      }

      if (realEstateId) {
        const request = mapRealEstateFormValuesToUpdateDto(
          riskId,
          transformedValues,
          survey.id,
          person.id,
          realEstateId,
          propertyTypeAnswerValue || transformedValues.propertyType
        );
        await realEstateAPI.updateRealEstateWithQuestions(request);
      } else {
        const request = mapRealEstateFormValuesToCreateDto(
          riskId,
          transformedValues,
          survey.id,
          person.id,
          propertyTypeAnswerValue,
          realEstate && realEstate.rentAmount,
          realEstate && realEstate.charges,
        );
        await realEstateAPI.createRealEstateWithQuestions(request);
      }

      refreshRealEstates && refreshRealEstates();
    } catch (error: any) {
      console.error("Error submitting real estate:", error);
      toast.error(error?.response?.error?.message || "An error occurred");
    }
  }, [getSurvey, person, realEstate, realEstateId, refreshRealEstates, transformFormValues, riskId, reconcileTypeBienInSurveyForm]);

  const handleDelete = useCallback(async (): Promise<void> => {
    if (realEstate && person?.id) {
      try {
        if (parcId) {
          // Contexte parc : on retire le bien du parc uniquement, sans le délier de la personne
          await realEstateAPI.removeBienFromParc({ parcId, bienId: realEstate.id });
        } else {
          // Contexte personne : on délie le bien de la personne
          await realEstateAPI.removePersonFromRealEstate({
            bienId: realEstate.id,
            personneId: person.id
          });
        }
        closePanel();
        refreshRealEstates && refreshRealEstates();
      } catch (error: any) {
        console.error("Error deleting real estate:", error);
        toast.error(error?.response?.error?.message || "An error occurred while deleting");
      }
    }
  }, [closePanel, parcId, person, realEstate, refreshRealEstates]);

  useEffect(() => {
    // In edit mode, disable button if no changes
    const isSubmitDisabled = !isFormValid || (isEditMode && !hasFormChanges);

    const basePanelActions = {
      closeLabel: "COMMON.CANCEL",
      submitLabel: "COMMON.VALIDATE",
      submitDisabled: isSubmitDisabled,
      onSubmit: showConfirmationDialog,
    };

    if (realEstate) {
      setPanelActions({
        ...basePanelActions,
        onDelete: handleDelete,
        deleteLabel: "COMMON.DELETE"
      });
    } else {
      setPanelActions(basePanelActions);
    }
  }, [closePanel, setPanelActions, realEstate, handleDelete, isFormValid, isEditMode, hasFormChanges, showConfirmationDialog]);

  useEffect(() => {
    if (realEstateId)
      fetchRealEstateById(realEstateId);
  }, [realEstateId, fetchRealEstateById]);

  const initialValues = useMemo(() => {
    if (!realEstate) {
      if (branchCode === BranchCode.RGI)
        return rgiRealEstateFormConfig.initialValues;
      return upiRealEstateFormConfig.initialValues;
    }
    const surveyForm = questionAnswers && mapToSurveyFormValue(questionAnswers)
    if (branchCode === BranchCode.RGI)
      return mapRealEstate<RgiRealEstateFormValues>(realEstate);
    return mapRealEstate<UpiRealEstateFormValues>(realEstate, surveyForm);
  }, [realEstate, questionAnswers, mapToSurveyFormValue, branchCode]);

  const validationSchema = useMemo(() => {
    if (branchCode === BranchCode.RGI)
      return rgiRealEstateFormConfig.validationSchema;
    return upiRealEstateFormConfig.validationSchema;
  }, [branchCode]);

  return {
    initialValues,
    submitRealEstate,
    validationSchema: validationSchema,
    realEstate
  };
};
